import { auth } from "../config/firebase";
import { hideSnackbar, showSnackbar } from "../features/snackbar";
import {
  Page,
  Severity,
  SubscriptionStatus,
} from "@beyondrealityapp/core/shared/constants";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFetchUserQuery } from "../api/user";
import { useLocation, useNavigate } from "react-router";

const ACTIVE_STATUSES: string[] = [
  SubscriptionStatus.ACTIVE,
  SubscriptionStatus.TRIALING,
  SubscriptionStatus.EARLY_ADOPTER,
  SubscriptionStatus.CANCEL_PENDING,
];

type UserProviderProps = {
  children: React.ReactNode;
};

const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: user } = useFetchUserQuery(auth.currentUser?.uid ?? "");

  const logOut = async () => {
    dispatch(hideSnackbar());
    auth
      .signOut()
      .then(() => {
        navigate(Page.SIGN_IN);
      })
      .catch((error) => {
        dispatch(
          showSnackbar({
            severity: Severity.ERROR,
            message: error.message,
          })
        );
      });
  };

  useEffect(() => {
    const checkBuddy = async () => {
      if (user && user.sharedWithMe.length > 0) {
        const idTokenResult = await auth.currentUser?.getIdTokenResult();
        const customClaimsBuddies = idTokenResult?.claims.buddies as
          | string[]
          | undefined;
        const hasAllBuddiesInCustomClaims = user.sharedWithMe.every(
          (buddy) => customClaimsBuddies?.includes(buddy.uid) ?? false
        );

        if (!customClaimsBuddies || !hasAllBuddiesInCustomClaims) {
          dispatch(
            showSnackbar({
              severity: Severity.WARNING,
              message:
                "You've been added as a buddy. Please log out and log back in to see your buddy's goals.",
              action: {
                label: "Log out",
                onClick: logOut,
              },
            })
          );
        }
      }
    };
    checkBuddy();

    // Redirect to onboarding when the user hasn't set their name yet.
    // If custom claims already exist and the subscription is NOT active
    // (e.g. expired), skip the redirect so SubscriptionGuard can send
    // them to /subscription-blocked instead.
    const redirectToOnboarding = async () => {
      if (!user || user.name !== "" || location.state?.fromOnboarding) return;

      const idToken = await auth.currentUser?.getIdTokenResult();
      const status = idToken?.claims.subscriptionStatus as string | undefined;

      // No claims yet (fresh sign-up) or active subscription → onboarding
      // Claims exist but subscription inactive → let SubscriptionGuard handle
      if (!status || ACTIVE_STATUSES.includes(status)) {
        navigate("/onboarding");
      }
    };
    redirectToOnboarding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  return <>{children}</>;
};

export default UserProvider;
